<script setup lang="ts">
/**
 * Haut de page « comparatif » des pages charte « apple » (Karim 2026-10-18) :
 * pastille, grand titre centré (slot `titre`, pour un mot en bleu), chapô,
 * bouton d'inscription + lien vers le comparatif, garanties en ligne, puis
 * la vraie page de don sur ordinateur et l'appli sur téléphone
 * (SceneOrdinateurTelephone). Entrée en CSS pur.
 */
import { TITRE_APPLE } from "~/utils/titresApple";

const props = defineProps<{
  surtitre: string;
  description: string;
  cta: string;
  ctaSource: string;
  lienTexte?: string;
  lienHref?: string;
  /** Garanties affichées sous les boutons (« 0 % de commission »…). */
  garanties?: string[];
  imageTelephone: string;
  altOrdinateur: string;
  altTelephone: string;
}>();

const registerLink = useRegisterLink();
</script>

<template>
  <section class="overflow-hidden px-4 pt-32 pb-28 sm:px-8 lg:pt-40 lg:pb-36">
    <div class="mx-auto max-w-[920px] text-center">
      <p class="entree mb-5 inline-flex items-center gap-1.5 rounded-full bg-[#f5f5f7] px-3.5 py-1.5 text-[13px] font-semibold text-(--color-main)">
        <UIcon name="i-lucide-scale" class="size-4" />
        {{ props.surtitre }}
      </p>
      <h1
        class="entree mx-auto mb-6 text-[40px] text-[#1d1d1f] [text-wrap:balance] sm:text-[60px] lg:text-[72px]"
        style="--d: 80ms"
        :style="TITRE_APPLE"
      >
        <slot name="titre" />
      </h1>
      <p class="entree mx-auto mb-9 max-w-[700px] text-[19px] font-medium leading-[1.45] text-[#424245] sm:text-[21px]" style="--d: 160ms">
        {{ props.description }}
      </p>
      <div class="entree flex flex-wrap items-center justify-center gap-x-8 gap-y-4" style="--d: 240ms">
        <BoutonApple v-register-cta="props.ctaSource" :to="registerLink" grand>{{ props.cta }}</BoutonApple>
        <LienApple v-if="props.lienTexte && props.lienHref" :to="props.lienHref">{{ props.lienTexte }}</LienApple>
      </div>
      <ul v-if="props.garanties?.length" class="entree mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2" style="--d: 280ms">
        <li v-for="g in props.garanties" :key="g" class="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#6e6e73]">
          <UIcon name="i-lucide-check" class="size-4 text-(--color-main)" aria-hidden="true" />{{ g }}
        </li>
      </ul>
    </div>

    <SceneOrdinateurTelephone
      class="entree"
      style="--d: 320ms"
      image-ordinateur="/billetterie/page-don.webp"
      :largeur-ordinateur="2000"
      :hauteur-ordinateur="1188"
      :alt-ordinateur="props.altOrdinateur"
      :image-telephone="props.imageTelephone"
      :largeur-telephone="662"
      :hauteur-telephone="1447"
      :alt-telephone="props.altTelephone"
      adresse="donoor.org/c/sauvons-la-11e-edition-du-festival-les-aiguilleurs"
    />
  </section>
</template>

<style scoped>
.entree {
  animation: entree 900ms cubic-bezier(0.28, 0, 0.22, 1) both;
  animation-delay: var(--d, 0ms);
}
@keyframes entree {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .entree {
    animation: none;
  }
}
</style>
